import { ApiError } from './api';
import type { TranslateFn, TranslationKey } from '../i18n';

/**
 * Maps the API's stable machine-readable error codes onto translated messages.
 *
 * The Worker always returns `{ success: false, error: { code, message } }`.
 * Localising by `code` keeps user-facing text in the locale files, while the
 * untranslated `message` remains a safe fallback for codes this map doesn't know.
 */
const CODE_TO_KEY: Record<string, TranslationKey> = {
  NETWORK_ERROR: 'api.networkError',
  REQUEST_FAILED: 'api.requestFailed',
  INTERNAL_ERROR: 'api.internalError',
  INVALID_CREDENTIALS: 'api.invalidCredentials',
  EMAIL_TAKEN: 'api.emailTaken',
  ACCOUNT_INACTIVE: 'api.accountInactive',
  SESSION_EXPIRED: 'api.sessionExpired',
  SESSION_INVALID: 'api.sessionExpired',
  NO_SESSION: 'api.sessionExpired',
  UNAUTHORIZED: 'api.unauthorized',
  FORBIDDEN: 'api.forbidden',
  EMAIL_INVALID: 'api.invalidEmail',
  PASSWORD_INVALID: 'api.weakPassword',

  // Phase 2: contest world.
  CONTEST_NOT_FOUND: 'api.contestNotFound',
  REGISTRATION_CLOSED: 'api.registrationClosed',
  CONTEST_FULL: 'api.contestFull',
  NOT_ENROLLED: 'api.notEnrolled',
  NOT_ACTIVE: 'api.notActive',
  ENTRY_NOT_CONFIRMED: 'api.entryNotConfirmed',
  CONTEST_NOT_LIVE: 'api.contestNotLive',
  ALREADY_ANSWERED: 'api.alreadyAnswered',
  SUBMISSION_REJECTED: 'api.submissionRejected',
  ANSWER_INVALID: 'api.answerInvalid',
  RESULTS_NOT_PUBLISHED: 'api.resultsNotPublished',
  RATE_LIMITED: 'api.rateLimited',
  PAYMENT_NOT_FOUND: 'api.paymentNotFound',
  PAYOUT_NOT_FOUND: 'api.payoutNotFound',
  NOT_FINALIZED: 'api.notFinalized',
  REASON_REQUIRED: 'api.reasonRequired',
  IDEMPOTENCY_KEY_REUSED: 'api.idempotencyKeyReused',
  CONTEST_NOT_EDITABLE: 'api.contestNotEditable',
  CONTENT_LOCKED: 'api.contentLocked',
  INVALID_AMOUNT: 'api.invalidAmount',
  ASSET_RAIL_MISMATCH: 'api.assetRailMismatch',
  UNKNOWN_ASSET: 'api.unknownAsset',
  INVALID_WINDOW: 'api.invalidWindow',
  CONTEST_HAS_NO_QUESTIONS: 'api.contestHasNoQuestions',
  QUESTION_WITHOUT_ANSWER_KEY: 'api.questionWithoutAnswerKey',
  ANSWER_KEY_REQUIRED: 'api.answerKeyRequired',
  OPTIONS_REQUIRED: 'api.optionsRequired',
  TRANSITION_CONTENDED: 'api.transitionContended',
  INVALID_TRANSITION: 'api.invalidTransition',
  NOT_FLAGGED: 'api.notFlagged',
};

/** Turn any thrown value into a message safe to show the user. */
export function apiErrorMessage(error: unknown, t: TranslateFn): string {
  if (error instanceof ApiError) {
    const key = CODE_TO_KEY[error.code];
    if (key) return t(key);
    return error.message || t('api.requestFailed');
  }
  return t('api.requestFailed');
}
